import { Pipe, PipeTransform } from '@angular/core';
import { NgxLinkifyjsService } from 'ngx-linkifyjs-v2';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

@Pipe({
  name: 'applinkifyHtml',
})
export class NgxLinkifyjsHtmlPipe implements PipeTransform {
  constructor(
    public linkifyService: NgxLinkifyjsService,
    private sanitizer: DomSanitizer
  ) {}

  transform(
    value: string,
    options?: { limit?: boolean; limitChars?: number }
  ): SafeHtml {
    if (!value) return value;

    // Step 1: Create a DOM parser to handle HTML safely
    const parser = new DOMParser();
    const doc = parser.parseFromString(value, 'text/html');

    // Step 2: Recursively process text nodes for links and mentions
    this.processNode(doc.body);

    let transformedHtml = doc.body.innerHTML;

    // Step 3: Apply the character limit if enabled
    if (options?.limit) {
      const limitChars = options?.limitChars || 40;
      const truncatedHtml = this.truncateHtml(transformedHtml, limitChars);

      // Add ellipsis only if truncation occurs
      return this.sanitizer.bypassSecurityTrustHtml(
        truncatedHtml.truncated
          ? truncatedHtml.html + '...'
          : truncatedHtml.html
      );
    }

    // Step 4: Return sanitized HTML if no truncation
    return this.sanitizer.bypassSecurityTrustHtml(transformedHtml);
  }

  private processNode(node: Node) {
    const children = Array.from(node.childNodes);

    children.forEach((child) => {
      if (child.nodeType === Node.TEXT_NODE) {
        this.linkifyTextNode(child);
      } else if (child.nodeType === Node.ELEMENT_NODE) {
        const element = child as HTMLElement;
        // Skip linkification if we are already inside an <a> tag
        if (element.tagName.toLowerCase() !== 'a') {
          this.processNode(element);
        }
      }
    });
  }

  private linkifyTextNode(node: Node) {
    const text = node.textContent || '';
    if (!text.trim()) return;

    // Step 1: Replace mentions first (mentions in plain text are usually @username)
    // Note: We handle mentions in plain text differently than the existing span-based HTML mentions
    let html = text.replace(/@([\w._]+)/g, (match, username) => {
      return `<span class="mention"><a href="/profile/view-profile/${username}" class="linkify linkify-mention">@${username}</a></span>`;
    });

    // Step 2: Find and replace links
    const links = this.linkifyService.find(html);

    // Sort links by length descending to avoid partial replacements if URLs are overlapping (rare but safe)
    links.sort((a, b) => b.value.length - a.value.length);

    links.forEach((link) => {
      const escapedLinkValue = this.escapeSpecialCharacters(link.value);
      // We use a regex that ensures we aren't replacing something already inside a tag we just created
      html = html.replace(
        new RegExp(`\\b${escapedLinkValue}\\b`, 'g'),
        `<a href="${link.href}" target="_blank" rel="noopener noreferrer">${link.value}</a>`
      );
    });

    // If we made changes, replace the text node with HTML
    if (html !== text) {
      const fragment = document.createRange().createContextualFragment(html);
      node.parentNode?.replaceChild(fragment, node);
    }
  }

  private replaceMentions(html: string): string {
    // Update the regular expression to include dots (.) and underscores (_)
    return html.replace(
      /<span class="mention" data-mention="(@[\w._]+)">(.+?)<\/span>/g,
      (match, mention) => {
        const username = mention.substring(1); // Remove the '@' symbol
        return `<span class="mention"><a href="/profile/view-profile/${username}" class="linkify linkify-mention">${mention}</a></span>`;
      }
    );
  }

  // Helper function to escape special characters in the link value
  private escapeSpecialCharacters(value: string): string {
    return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  // Function to safely truncate HTML at the last full word or tag
  private truncateHtml(
    html: string,
    maxLength: number
  ): { html: string; truncated: boolean } {
    // Decode HTML entities to ensure characters like `&nbsp;` are handled correctly
    const decodedHtml = this.decodeHtmlEntities(html);

    // Strip HTML tags to calculate the visible length
    const strippedHtml = decodedHtml.replace(/<[^>]*>/g, '');

    // If the content is shorter than the max length, return the original HTML
    if (strippedHtml.length <= maxLength) {
      return { html, truncated: false };
    }

    // Truncate the visible text to the limit
    let truncatedText = strippedHtml.substring(0, maxLength);

    // Step 1: Rebuild the HTML structure with truncated visible text
    let truncatedHtml = this.buildTruncatedHtml(html, truncatedText);

    // Step 2: Return truncated HTML with ellipsis if necessary
    return { html: truncatedHtml, truncated: true };
  }

  // Helper function to decode HTML entities (e.g., &nbsp; => " ")
  private decodeHtmlEntities(text: string): string {
    const doc = new DOMParser().parseFromString(text, 'text/html');
    return doc.documentElement.textContent || '';
  }

  // Helper function to rebuild the HTML after truncation
  private buildTruncatedHtml(
    originalHtml: string,
    truncatedText: string
  ): string {
    let resultHtml = '';
    let remainingText = truncatedText;
    let lastIndex = 0;

    // Iterate through the original HTML to rebuild it
    let match;
    const regex = /(<[^>]+>)/g; // This regex matches any HTML tag

    // Iterate through the string and keep track of where we are in the original HTML
    while ((match = regex.exec(originalHtml)) !== null) {
      // Get the tag before the next match
      const tag = match[0];

      // Find the position where the tag starts in the original HTML
      const tagStartIndex = match.index;

      // Add content before the tag (the text part)
      const textContent = originalHtml.slice(lastIndex, tagStartIndex);
      lastIndex = tagStartIndex + tag.length;

      if (remainingText.length > textContent.length) {
        resultHtml += textContent + tag;
        remainingText = remainingText.slice(textContent.length);
      } else {
        resultHtml += remainingText;
        return resultHtml;
      }
    }

    // Add the final part of the text if there's anything left
    if (remainingText.length > 0) {
      resultHtml += remainingText;
    }

    return resultHtml;
  }
}
